import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { RadioCircle, RadioInput, Textarea } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import SimpleUploadedImageRow from '../SimpleUploadedImageRow';
import UploadMultipleImages from '../UploadMultipleImages';
import UploadSingleImage from '../UploadSingleImage';
import {
  AddMoreButton,
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  ImagesBox,
  LabelText,
  MoreImages,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import { GridInner, InputsGrid, NameTextArea } from '../VariationThree';
import { ImageGrid, TextAreaBox } from '../VariationTwo';
import { getWatermarkPosition, getWatermarkTag } from '../shared';
import Filler from '../shared/Filler';
import {
  ActionBox,
  ActionTitle,
  StyledRadiosLabel,
} from '../shared/styledComponents';
import GeneratedImages from './GeneratedImages';
import UploadedImageRow from './UploadedImageRow';
import VariationsStyles from './VariationStyles';

export const CHANNEL_NAME_COLORS = ['black', 'blue'];

export const CHANNEL_IMAGE_OPTIONS = ['custom', 'reddit', 'anonymous'];

export interface ImageVariation {
  leftImageSrc: string;
  rightImageSrc?: string;
  rightText: string;
  leftWatermarkTag: string;
  leftWatermarkPosition: string;
  rightWatermarkTag?: string;
  rightWatermarkPosition?: string;
  textPosition: string;
  authorImage?: string;
  divider: string;
  authorImageOriginal?: ImageData[];
  authorName: string;
  channelNameColor: string;
  brakes: boolean;
  channelImageOption: string;
}

const StyledUploadedImages = styled(UploadedImages)`
  height: 360px;
`;

const StyledUploadMultipleImages = styled(UploadMultipleImages)`
  height: 209px;
`;

const UploadAuthorImages = styled(UploadSingleImage)`
  height: 98px;
`;

const ChannelImageHolder = styled.div`
  margin-top: 16px;
`;

const ChannelNameHolder = styled.div`
  margin-top: 16px;
`;

const HeadingsHolder = styled.div`
  flex: 1;
`;

const StyledTextAreaBox = styled(TextAreaBox)`
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 4px;
`;

const StyledTextArea = styled(Textarea)`
  height: 100px;
`;

interface Props {
  inArticle?: boolean;
}

const VariationImgImgComment: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);
  const [brakes, setBrakes] = useState(false);
  const [text, setText] = useState('');
  const [leftImages, setLeftImages] = useState<ImageData[]>([]);
  const [rightImages, setRightImages] = useState<ImageData[]>([]);
  const [showMoreImagesModal, setShowMoreImagesModal] = useState(false);
  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [authorImages, setAuthorImages] = useState<ImageData[]>([]);
  const [channelImageOption, setChannelImageOption] = useState('custom');
  const [showRightImagesModal, setShowRightImagesModal] = useState(false);

  const [authorName, setAuthorName] = useState('');
  const [nameColor, setNameColor] = useState('black');
  const [images, setImages] = useState<string[]>();

  const onReset = () => {
    setLeftImages([]);
    setRightImages([]);
    setVariations([]);
    setImages([]);
    setAuthorImages([]);
    setBrakes(false);
    setText('');
    setAuthorName('');
    setNameColor('black');
    setChannelImageOption('custom');
  };

  const generateVariations = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const newVariations = leftImages.flatMap((leftImage) =>
      rightImages.map((rightImage) => ({
        leftImageSrc:
          leftImage.croppedImage ?? URL.createObjectURL(leftImage.image),
        rightImageSrc:
          rightImage.croppedImage ?? URL.createObjectURL(rightImage.image),
        rightText: text,
        brakes: brakes,
        textPosition: leftImage.textPosition,
        leftWatermarkTag: getWatermarkTag(leftImage),
        leftWatermarkPosition: getWatermarkPosition(leftImage),
        rightWatermarkTag: getWatermarkTag(rightImage),
        rightWatermarkPosition: getWatermarkPosition(rightImage),
        divider: leftImage.divider,
        authorName: authorName,
        authorImage: !!authorImages.length
          ? authorImages[0]?.croppedImage ??
            URL.createObjectURL(authorImages[0].image)
          : undefined,
        channelNameColor: nameColor,
        channelImageOption: channelImageOption,
      })),
    );

    setVariations(newVariations);
  };

  const renderLeftImages = leftImages.map((image, index) => (
    <UploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      type="left"
      setImages={setLeftImages}
    />
  ));

  const renderRightImages = rightImages.map((image, index) => (
    <UploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      type="right"
      setImages={setRightImages}
    />
  ));

  const renderAuthorImages = authorImages.map((image, index) => (
    <SimpleUploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setAuthorImages}
    />
  ));

  const isResetDisabled =
    !leftImages?.length &&
    !rightImages?.length &&
    !text &&
    !brakes &&
    !authorImages.length &&
    !authorName &&
    nameColor === 'black' &&
    channelImageOption === 'custom';

  return (
    <>
      <Box>
        <Header>
          <HeadingsHolder>
            <Heading $type="button"> Img + img + Comment</Heading>
            <Text $type="smallHeadline" $color="gray">
              Attach twi half images and add text with the necessary parameters.
              Additionally, include the author's image and name
            </Text>
          </HeadingsHolder>
          <HeaderButtons>
            <ResetButton
              color="red"
              disabled={isResetDisabled}
              onClick={onReset}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <>
            <ImageGrid>
              <ImagesBox $hasImages={!!leftImages?.length}>
                <LabelText $type="smallHeadline">Left Images</LabelText>
                {!!leftImages?.length ? (
                  <>
                    <StyledUploadedImages>
                      {renderLeftImages}
                    </StyledUploadedImages>
                    <MoreImages>
                      <AttachedCountBadge count={leftImages.length} />
                      <AddMoreButton
                        onClick={() => setShowMoreImagesModal(true)}
                        color="colorized"
                      >
                        Add more images
                      </AddMoreButton>
                    </MoreImages>
                  </>
                ) : (
                  <StyledUploadMultipleImages
                    coverIndex={1}
                    setImages={setLeftImages}
                  />
                )}
              </ImagesBox>
              <ImagesBox
                $hasImages={!!rightImages?.length}
                $hasLeftBorder={true}
              >
                <LabelText $type="smallHeadline">Right Images</LabelText>
                {!!rightImages?.length ? (
                  <>
                    <StyledUploadedImages>
                      {renderRightImages}
                    </StyledUploadedImages>
                    <MoreImages>
                      <AttachedCountBadge count={rightImages.length} />
                      <AddMoreButton
                        onClick={() => setShowRightImagesModal(true)}
                        color="colorized"
                      >
                        Add more images
                      </AddMoreButton>
                    </MoreImages>
                  </>
                ) : (
                  <StyledUploadMultipleImages
                    coverIndex={3}
                    setImages={setRightImages}
                    noneDivider
                  />
                )}
              </ImagesBox>
            </ImageGrid>
            <StyledTextAreaBox>
              <div>
                <LabelText $type="smallHeadline">Right Text</LabelText>
                <StyledTextArea
                  onChange={(e) => setText(e.target.value)}
                  value={text}
                  placeholder="Add text"
                ></StyledTextArea>
              </div>
              <div>
                <ActionTitle $type="smallHeadline">Text Parameters</ActionTitle>
                <ActionBox>
                  <Checkbox
                    checked={brakes}
                    onChange={() => setBrakes((prev) => !prev)}
                  >
                    Custom brakes
                  </Checkbox>
                </ActionBox>
              </div>
            </StyledTextAreaBox>
            <InputsGrid>
              <GridInner $noBottomPadding={!!authorImages?.length}>
                <LabelText $type="smallHeadline">Channel Image</LabelText>
                <ActionBox>
                  {CHANNEL_IMAGE_OPTIONS.map((option) => (
                    <StyledRadiosLabel key={option}>
                      {option}
                      <RadioInput
                        type="radio"
                        name="channel_image"
                        checked={channelImageOption === option}
                        onChange={() => setChannelImageOption(option)}
                      />
                      <RadioCircle />
                    </StyledRadiosLabel>
                  ))}
                </ActionBox>
                {channelImageOption === 'custom' && (
                  <ChannelImageHolder>
                    <LabelText $type="smallHeadline">
                      Add Channel Image
                    </LabelText>
                    {!!authorImages?.length ? (
                      <UploadedImages>{renderAuthorImages}</UploadedImages>
                    ) : (
                      <UploadAuthorImages
                        coverIndex={2}
                        setImages={setAuthorImages}
                      />
                    )}
                  </ChannelImageHolder>
                )}
              </GridInner>
              <GridInner>
                <LabelText $type="smallHeadline">Channel Name Color</LabelText>
                <ActionBox>
                  {CHANNEL_NAME_COLORS.map((color) => (
                    <StyledRadiosLabel key={color}>
                      {color}
                      <RadioInput
                        type="radio"
                        name="name_color"
                        checked={nameColor === color}
                        onChange={() => setNameColor(color)}
                      />
                      <RadioCircle />
                    </StyledRadiosLabel>
                  ))}
                </ActionBox>
                <ChannelNameHolder>
                  <LabelText $type="smallHeadline">Channel Name</LabelText>
                  <NameTextArea
                    placeholder="Add Name"
                    onChange={(e) => setAuthorName(e.target.value)}
                    value={authorName}
                  ></NameTextArea>
                </ChannelNameHolder>
              </GridInner>
            </InputsGrid>
          </>
        )}
      </Box>
      <VariationsStyles />
      <Filler />
      {showContent && (
        <ButtonHolder>
          <Button
            disabled={!leftImages?.length || !rightImages?.length}
            onClick={generateVariations}
          >
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
      <AnimatePresence>
        {!!showMoreImagesModal && (
          <MoreImagesModal
            onClose={() => setShowMoreImagesModal(false)}
            setImages={setLeftImages}
            coverIndex={1}
          />
        )}
        {!!showRightImagesModal && (
          <MoreImagesModal
            onClose={() => setShowRightImagesModal(false)}
            setImages={setRightImages}
            coverIndex={3}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationImgImgComment;
